import { useMemo } from "react"
import { type OperationContext, type OperationResult, useMutation, useQuery } from "urql"

import { FULL_LIST_LIMIT } from "@/constants/common"
import { AddCategoryDocument, ProductCategoriesDocument, UpdateCategoryDocument } from "@/graphql/codegen/graphql"
import type {
  Category,
  CategoryMutationCreateArgs,
  CategoryMutationUpdateArgs,
  Mutation,
  Query,
} from "@/graphql/codegen/graphql"

const useProductCategory = ({
  query = "",
  categoryId = "",
  pause = false,
}: {
  query: string
  categoryId?: string
  pause?: boolean
}): {
  productCategoryList: Category[]
  productCategoryListFetching: boolean

  createCategory: (
    variables: CategoryMutationCreateArgs,
    context?: Partial<OperationContext>
  ) => Promise<OperationResult<Pick<Mutation, "category">, CategoryMutationCreateArgs>>
  updateCategory: (
    variables: CategoryMutationUpdateArgs,
    context?: Partial<OperationContext>
  ) => Promise<OperationResult<Pick<Mutation, "category">, CategoryMutationUpdateArgs>>
} => {
  const [{ data, fetching: productCategoryListFetching }] = useQuery<Pick<Query, "category">>({
    query: ProductCategoriesDocument,
    variables: {
      input: {
        filter: {
          ...(query && query?.length > 1 && { query }),
          ...(categoryId && categoryId?.length && { categoryId }),
        },
        limit: FULL_LIST_LIMIT,
        offset: 0,
      },
    },
    pause,
  })

  const productCategoryList = useMemo(() => {
    if (!data?.category?.list) {
      return []
    }
    return data.category.list.__typename === "CategoryListSuccess" ? data.category.list.categories : []
  }, [data])

  // Mutation to create a new category
  const [, createCategory] = useMutation<Pick<Mutation, "category">, CategoryMutationCreateArgs>(AddCategoryDocument)

  // Mutation to update a category
  const [, updateCategory] = useMutation<Pick<Mutation, "category">, CategoryMutationUpdateArgs>(UpdateCategoryDocument)

  return {
    productCategoryList,
    productCategoryListFetching,
    createCategory,
    updateCategory,
  }
}

export default useProductCategory
